import { isNumber, isString } from "remeda";

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
}).format;

export function formatCurrency(value?: number | string, fallback = "--") {
  let transformedValue = value;
  if (isString(value)) {
    transformedValue = Number(value);
  }
  if (!isNumber(transformedValue)) return fallback;
  return currencyFormatter(transformedValue);
}

const phoneNumberRegex = /^\+1(\d{3})(\d{3})(\d{4})/;

export const formatPhoneNumber = <TValue extends string | undefined>(
  value: TValue,
  options?: { hasNoPrefix?: boolean },
): TValue => {
  if (!value) return value;

  const match = phoneNumberRegex.exec(value.startsWith("+") ? value : `+1${value}`);
  if (!match) return value;

  if (options?.hasNoPrefix) return `(${match[1]}) ${match[2]} ${match[3]}` as TValue;
  return `+1 (${match[1]}) ${match[2]}-${match[3]}` as TValue;
};

/**
 * Returns a string description of the length of time between the given start and end Dates.
 * `style: "compact"` renders `"1h 30m"` (omitting zero parts, `"0m"` when both are zero);
 * the default `"long"` style renders `"1 hours 30 minutes"`.
 */
export const generateTimeDifferenceLabel = (endTime: Date, startTime: Date, style: "long" | "compact" = "long") => {
  if (Number.isNaN(endTime.getTime()) || Number.isNaN(startTime.getTime())) return;
  if (startTime.getTime() > endTime.getTime()) {
    console.warn("generateTimeDifferenceLabel: startTime is after endTime, dates may be switched");
  }
  const minutes = Math.abs(endTime.getTime() - startTime.getTime()) / 60000;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);
  if (style === "compact") {
    return [hours && `${hours}h`, remainingMinutes && `${remainingMinutes}m`].filter(Boolean).join(" ") || "0m";
  }
  return `${hours} hours ${remainingMinutes} minutes`;
};

/**
 * Returns the first letter uppercase and the rest lowercase.
 */
export function titleCase(text: string): string {
  return text.length > 0 ? `${text[0].toUpperCase()}${text.slice(1).toLowerCase()}` : text;
}

/**
 * Lowercases and dashes a string for use as a filename or URL segment.
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replaceAll(/^-|-$/g, "");
}

export const METERS_PER_MILE = 1609.344;

/**
 * Formats a distance in metres as a US-mile string. Sub-0.1mi values collapse
 * to `< 0.1 mi` so admin/manager UIs don't display "0.0 mi". `null`/`undefined`
 * input renders as the fallback (default `—`).
 */
export function formatDistanceMiles(meters: number | null | undefined, fallback = "—"): string {
  if (meters == null) return fallback;
  const miles = meters / METERS_PER_MILE;
  if (miles < 0.1) return "< 0.1 mi";
  return `${miles.toFixed(1)} mi`;
}

export const formatCount = (count: number) => count.toLocaleString("en-US");

export const capitalize = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  count === 1 ? singular : plural;
